import React, { useMemo, useState, useCallback } from 'react';
import { Tooltip } from 'react-tooltip';
import { Copy, Check, ArrowUp, ArrowDown, ArrowLeftRight } from 'lucide-react';

const formatNumber = (num) => new Intl.NumberFormat().format(num);

const formatAverage = (num) => 
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }).format(num);

const getPriceDiffInfo = (current, average) => {
  if (!average || average === 0) return { percent: 0, color: 'text-gray-400' };
  const diffPercent = ((current - average) / average) * 100;
  const color = Math.abs(diffPercent) <= 2 
    ? 'text-gray-400' 
    : diffPercent > 0 
      ? 'text-red-500'
      : 'text-green-500';
  return { percent: diffPercent, color };
};

const ItemImage = ({ itemId, name }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  return (
    <div className="item_icon" style={{ position: 'relative' }}>
      {!imageLoaded && (
        <img
          className="item_icon"
          src="/items.png"
          alt="Item placeholder"
          style={{ position: 'absolute' }}
        />
      )}
      <img 
        className="item_icon"
        style={{ opacity: imageLoaded ? 1 : 0, transition: 'opacity 0.2s' }}
        src={`https://imageserver.eveonline.com/Type/${itemId}_64.png`}
        alt={name || 'Item icon'}
        loading="lazy"
        onLoad={handleImageLoad}
      />
    </div>
  );
};


const ProcessingResults = ({ 
  raw_results, 
  processed_results, 
  leftover_items,
  total_sell_value_raw,
  total_buy_value_raw,
  total_vol_raw,
  total_sell_value_processed,
  total_buy_value_processed,
  total_vol_processed,
  market,
  market_status,
  appraisal_id,
  appraisaDate,
  yield_percentage,
}) => {

  const [tooltipText, setTooltipText] = useState('');
  const [tooltipId, setTooltipId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const combinedResults = [...processed_results, ...leftover_items];
  const total_sell_value_combined = combinedResults.reduce((acc, item) => acc + (item.total_sell_price || 0), 0);
  const total_buy_value_combined = combinedResults.reduce((acc, item) => acc + (item.total_buy_price || 0), 0);
  const total_vol_combined = combinedResults.reduce((acc, item) => acc + (item.total_item_vol || 0), 0);
  const split_value_combined = (total_sell_value_combined + total_buy_value_combined) / 2;
  const sharableLink = `${window.location.origin}/processed/${appraisal_id}`;
  // Format the appraisal date
  const formattedDate = useMemo(() => {
    if (!appraisaDate) return '-';
    const created_at_new = new Date(appraisaDate);
    return Intl.DateTimeFormat("en-UK", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: false,
    }).format(created_at_new);
  }, [appraisaDate]);
  

  const copyAllMaterialsProcessed = () => {
    const materialsCopyText = combinedResults
      .map(item => `${item.name} x${formatNumber(item.quantity)}`)
      .join('\n');
    
    navigator.clipboard.writeText(materialsCopyText).then(() => {
      setCopiedId('processed-materials');
      setTooltipText('Copied!');
      setTooltipId('processed-materials');
      setTimeout(() => {
        setCopiedId(null);
        setTooltipId(null);
      }, 1500);
    }).catch((err) => {
      console.error('Failed to copy text:', err);
    });
  };

  const copyAllMaterialsRaw = () => {
    const materialsCopyText = raw_results
      .map(item => `${item.name} x${formatNumber(item.quantity)}`)
      .join('\n');
    
    navigator.clipboard.writeText(materialsCopyText).then(() => {
      setCopiedId('raw-materials');
      setTooltipText('Copied!');
      setTooltipId('raw-materials');
      setTimeout(() => {
        setCopiedId(null);
        setTooltipId(null);
      }, 1500);
    }).catch((err) => {
      console.error('Failed to copy text:', err);
    });
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTooltipText('Copied!');
      setTooltipId(id);
      setTimeout(() => {
        setCopiedId(null);
        setTooltipId(null);
      }, 1500);
    }).catch((err) => {
      console.error('Failed to copy text:', err);
    });
  };
  
  const renderMaterialsTable = (materials, title, totals, isCombinedTable) => (
    <div className="appraisal-results">
      <h3>{title}</h3>
      
      <div className="overflow-x-auto">
      <div className="grand-totals">
          <div 
            className="total-item" 
            data-tooltip-id="sell-total-tooltip" 
            data-tooltip-content={tooltipId === 'sell-total' ? tooltipText : ''} 
            onClick={() => handleCopy(Math.trunc(totals.total_sell_value), 'sell-total')} 
            style={{ cursor: 'pointer'}}
          >
            <strong>Total Sell Value</strong>
            {formatNumber(totals.total_sell_value)} ISK
          </div>
          <div 
            className="total-item" 
            data-tooltip-id="split-total-tooltip" 
            data-tooltip-content={tooltipId === 'split-total' ? tooltipText : ''} 
            onClick={() => handleCopy(Math.trunc(totals.split_value), 'split-total')} 
            style={{ cursor: 'pointer'}}
          >
            <strong>Split Value</strong>
            {formatNumber(totals.split_value)} ISK
          </div>
          <div 
            className="total-item" 
            data-tooltip-id="buy-total-tooltip" 
            data-tooltip-content={tooltipId === 'buy-total' ? tooltipText : ''} 
            onClick={() => handleCopy(Math.trunc(totals.total_buy_value), 'buy-total')} 
            style={{ cursor: 'pointer'}}
          >
            <strong>Total Buy Value</strong>
            {formatNumber(totals.total_buy_value)} ISK
          </div>
          <div 
            className="total-item" 
            data-tooltip-id="volume-total-tooltip" 
            data-tooltip-content={tooltipId === 'volume-total' ? tooltipText : ''} 
            onClick={() => handleCopy(`${formatNumber(totals.total_vol)} m³`, 'volume-total')} 
            style={{ cursor: 'pointer'}}
          >
            <strong>Total Volume</strong>
            {formatNumber(totals.total_vol)} m<sup>3</sup>
          </div>
          <div 
            className="total-item" 
            data-tooltip-id="date-tooltip" 
            data-tooltip-content={tooltipId === 'date' ? tooltipText : ''} 
            onClick={() => handleCopy(formattedDate, 'date')} 
            style={{ cursor: 'pointer'}}
          >
            <strong>Created at</strong>
            {formattedDate}
          </div>
          <div className="total-item flex justify-between" style={{ cursor: 'pointer' }}>
            <div
              className="flex-1"
              data-tooltip-id="market-tooltip" 
              data-tooltip-content={tooltipId === 'market' ? tooltipText : ''} 
              onClick={() => handleCopy(market.toUpperCase(), 'market')}
            >
              <strong>Market</strong>
              <div>{market.toUpperCase()}</div>
            </div>
            <div
              className="flex-1 ml-4 mr-8"
              data-tooltip-id="yield-tooltip" 
              data-tooltip-content={tooltipId === 'yield' ? tooltipText : ''} 
              onClick={() => handleCopy(`${yield_percentage.toFixed(2)}%`, 'yield')}
            >
              <strong>Processing Yield</strong>
              <div>{yield_percentage ? `${yield_percentage.toFixed(2)}%` : '-'}</div>
            </div>
          </div>
        </div>

        <table className="results-table">
          <thead>
            <tr>
              <th>
              <button
                  className="ml-4 mb-4 p-2 w-10 h-10 rounded-lg font-medium bg-neutral-900 hover:bg-orange-400 transition-colors flex items-center justify-center gap-5 text-orange-300 hover:text-neutral-900"
                  onClick={isCombinedTable ? copyAllMaterialsProcessed : copyAllMaterialsRaw}
                  data-tooltip-id={`${isCombinedTable ? 'processed' : 'raw'}-materials-tooltip`}
                  data-tooltip-content={tooltipId === `${isCombinedTable ? 'processed' : 'raw'}-materials` ? tooltipText : ''}
                >
                  {copiedId === `${isCombinedTable ? 'processed' : 'raw'}-materials` ? (
                    <Check size={24} />
                  ) : (
                    <Copy size={24} />
                  )}
                </button>
              </th>
              <th>Item Name</th>
              <th>Quantity</th>
              <th>Single Volume<br /> Total Volume</th>
              <th>Single Sell<br />Single Buy</th>
              <th>Total Sell <br /> Total Buy</th>
            </tr>
          </thead>
          <tbody>
            {materials.map((item, index) => (
              <tr key={`${item.item_id}-${index}`}>
                <td>
                  {item.item_id && (
                    <ItemImage itemId={item.item_id} name={item.name} />
                  )}
                </td>
                <td>{item.name || '-'}</td>
                <td>{formatNumber(item.quantity) || '-'}</td>
                <td>
                  {item.item_vol ? `${formatNumber(item.item_vol)} m³` : '-'}
                  <br />
                  {item.total_item_vol ? `${formatNumber(item.total_item_vol)} m³` : '-'}
                </td>
                <td>
                  <div className="flex items-center gap-1">
                    {item.sell_price !== null ? (
                      <>
                        {formatNumber(item.sell_price)}
                        {item.sell_average > 0 && (() => {
                          const { percent, color } = getPriceDiffInfo(item.sell_price, item.sell_average);
                          return (
                            <>
                              <span
                                className={`ml-1 flex items-center ${color}`}
                                data-tooltip-id={`sell-tooltip-${item.item_id}`}
                              >
                                {Math.abs(percent) <= 2 ? (
                                  <ArrowLeftRight size={14} />
                                ) : percent > 0 ? (
                                  <ArrowUp size={14} />
                                ) : (
                                  <ArrowDown size={14} />
                                )}
                              </span>
                              <Tooltip id={`sell-tooltip-${item.item_id}`} place="right" effect="solid">
                                <div>30-day average: {formatAverage(item.sell_average)} ISK</div>
                                <div className={color}>{Math.abs(percent).toFixed(1)}% {percent > 0 ? 'above' : 'below'} average</div>
                              </Tooltip>
                            </>
                          );
                        })()}
                      </>
                    ) : '-'}
                  </div>
                  <div className="flex items-center gap-1">
                    {item.buy_price !== null ? (
                      <>
                        {formatNumber(item.buy_price)}
                        {item.buy_average > 0 && (() => {
                          const { percent, color } = getPriceDiffInfo(item.buy_price, item.buy_average);
                          return (
                            <>
                              <span
                                className={`ml-1 flex items-center ${color}`}
                                data-tooltip-id={`buy-tooltip-${item.item_id}`}
                              >
                                {Math.abs(percent) <= 2 ? (
                                  <ArrowLeftRight size={14} />
                                ) : percent > 0 ? (
                                  <ArrowUp size={14} />
                                ) : (
                                  <ArrowDown size={14} />
                                )}
                              </span>
                              <Tooltip id={`buy-tooltip-${item.item_id}`} place="right" effect="solid">
                                <div>30-day average: {formatAverage(item.buy_average)} ISK</div>
                                <div className={color}>{Math.abs(percent).toFixed(1)}% {percent > 0 ? 'above' : 'below'} average</div>
                              </Tooltip>
                            </>
                          );
                        })()}
                      </>
                    ) : '-'}
                  </div>
                </td>
                <td>
                  {item.total_sell_price !== null ? formatNumber(item.total_sell_price) : '-'}
                  <br />
                  {item.total_buy_price !== null ? formatNumber(item.total_buy_price) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="w-full">

      {/* Combined Processed & Leftover Materials Section */}
      {renderMaterialsTable(combinedResults, "Processed & Leftover Materials", {
          total_vol: total_vol_combined,
          total_sell_value: total_sell_value_combined,
          total_buy_value: total_buy_value_combined,
          split_value: split_value_combined
        }, true)}

      {/*  Raw Materials Section*/}
      <div className="mt-8">
      {renderMaterialsTable(raw_results, "Raw Materials", {
        total_vol: total_vol_raw,
        total_sell_value: total_sell_value_raw,
        total_buy_value: total_buy_value_raw,
        split_value: (total_sell_value_raw + total_buy_value_raw) / 2
      }, false)}
      </div>
    </div>
  );
};

export default ProcessingResults;
